'use client'

import Image from 'next/image'
import { AnimatePresence, motion, useMotionValueEvent, useReducedMotion, useScroll, useTransform } from 'framer-motion'
import { useRef, useState } from 'react'

type ProcessStep = {
  id?: string
  stepTitle: string
  stepDescription: string
}

export default function CinematicProcessStory({
  image,
  imageAlt,
  title,
  lead,
  steps,
}: {
  image: string
  imageAlt: string
  title: string
  lead: string
  steps: ProcessStep[]
}) {
  const ref = useRef<HTMLElement>(null)
  const reduceMotion = useReducedMotion()
  const [phase, setPhase] = useState<'before' | 'pinned' | 'after'>('before')
  const [active, setActive] = useState(0)
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start start', 'end end'],
  })
  const imageScale = useTransform(scrollYProgress, [0, 1], reduceMotion ? [1, 1] : [1.16, 1])
  const imageY = useTransform(scrollYProgress, [0, 1], reduceMotion ? ['0%', '0%'] : ['-4%', '4%'])
  const veil = useTransform(scrollYProgress, [0, 0.5, 1], [0.28, 0.48, 0.68])

  useMotionValueEvent(scrollYProgress, 'change', (value) => {
    setPhase(value <= 0 ? 'before' : value >= 1 ? 'after' : 'pinned')
    setActive(Math.min(steps.length - 1, Math.floor(value * steps.length)))
  })

  if (!steps.length) return null

  return (
    <section
      ref={ref}
      className="relative border-y border-white/10 bg-[#071f2d] text-white"
      style={{ minHeight: `${Math.max(steps.length, 3) * 100}vh` }}
    >
      <div
        className={`h-screen w-full overflow-hidden ${
          phase === 'pinned'
            ? 'fixed inset-x-0 top-0 z-40'
            : phase === 'after'
              ? 'absolute inset-x-0 bottom-0'
              : 'absolute inset-x-0 top-0'
        }`}
      >
        <motion.div style={{ scale: imageScale, y: imageY }} className="absolute inset-0">
          <Image src={image} alt={imageAlt} fill sizes="100vw" className="object-cover" />
        </motion.div>
        <motion.div style={{ opacity: veil }} className="absolute inset-0 bg-[#071f2d]" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#071f2d]/95 via-[#071f2d]/62 to-transparent rtl:bg-gradient-to-l" />
        <div className="absolute inset-0 opacity-35 [background-image:linear-gradient(rgba(241,165,78,.1)_1px,transparent_1px),linear-gradient(90deg,rgba(241,165,78,.1)_1px,transparent_1px)] [background-size:64px_64px]" />

        <div className="relative z-10 mx-auto grid h-full max-w-7xl items-center gap-12 px-6 py-24 md:px-10 lg:grid-cols-[0.82fr_1.18fr]">
          <div>
            <p className="text-sm font-bold uppercase tracking-[0.24em] text-[#f1a54e]">{title}</p>
            <h2 className="mt-5 max-w-xl font-heading text-5xl font-black leading-none md:text-7xl">{title}</h2>
            <p className="mt-6 max-w-xl text-lg leading-relaxed text-white/68">{lead}</p>
            <div className="mt-10 flex gap-2">
              {steps.map((step, index) => (
                <span
                  key={step.id || `${step.stepTitle}-${index}`}
                  className={`h-1.5 flex-1 transition-colors duration-500 ${
                    index <= active ? 'bg-[#f1a54e]' : 'bg-white/18'
                  }`}
                />
              ))}
            </div>
          </div>

          <div className="relative min-h-[22rem]">
            <AnimatePresence mode="wait">
              <motion.article
                key={active}
                initial={reduceMotion ? { opacity: 1 } : { opacity: 0, y: 46, filter: 'blur(12px)' }}
                animate={{ opacity: 1, y: 0, filter: 'blur(0px)' }}
                exit={reduceMotion ? { opacity: 0 } : { opacity: 0, y: -36, filter: 'blur(10px)' }}
                transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
                className="absolute inset-0 flex flex-col justify-center border-s-2 border-[#f1a54e] bg-[#071f2d]/52 p-7 backdrop-blur-md md:p-10"
              >
                <span className="font-heading text-7xl font-black text-[#f1a54e]/35 md:text-9xl">
                  {String(active + 1).padStart(2, '0')}
                </span>
                <h3 className="mt-1 font-heading text-4xl font-black md:text-6xl">{steps[active].stepTitle}</h3>
                <p className="mt-6 max-w-2xl text-lg leading-relaxed text-white/74 md:text-xl">
                  {steps[active].stepDescription}
                </p>
              </motion.article>
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  )
}
